import './styles/tokens.css';
import './styles/reset.css';
import './styles/global.css';
import { api } from './api/client.js';
import { setToken, getToken, clearToken } from './api/session.js';
import { renderAuthView } from './ui/auth-view.js';
import { mountAppShell } from './ui/shell/app-shell.js';
import { setComposeCallbacks } from './ui/shell/floating-compose.js';
import { renderTodayView } from './ui/today-view.js';
import { renderForestView } from './ui/forest-view.js';
import { renderSettingsView } from './ui/settings-view.js';
import { setState } from './ui/views.js';
import { putMemo } from './storage/memosStore.js';
import { createMemo } from '@memori-web/shared';
import { runSync } from './sync/sync.js';
import { setSyncStatus } from './ui/components/sync-indicator.js';

async function bootstrap() {
  const root = document.getElementById('app');
  if (!root) {
    throw new Error('Root element #app not found');
  }

  // 0. /extension-auth (Chrome 拡張連携専用ページ)
  if (location.pathname === '/extension-auth') {
    const { renderExtensionAuthView } = await import('./ui/extension-auth-view.js');
    renderExtensionAuthView(root);
    return;
  }

  // 1. /auth/callback (Better-Auth verify からリダイレクトされてくる)
  //    Bearer プラグインがレスポンスに token を載せているので、getSession で取れる。
  if (location.pathname === '/auth/callback') {
    const session = await api.getSession();
    if (session) {
      setToken(session.token);
    }
    history.replaceState({}, '', '/');
  }

  // 2. token が無ければ auth view、あればセッション確認 → app-shell
  if (!getToken()) {
    renderAuthView(root);
    return;
  }
  const session = await api.getSession();
  if (!session) {
    clearToken();
    renderAuthView(root);
    return;
  }

  setState({
    activeTab: 'today',
    user: { id: session.user.id, email: session.user.email },
  });

  const triggerSync = async () => {
    setSyncStatus('syncing');
    try {
      await runSync(api);
      setSyncStatus('idle');
      const refresh = (window as unknown as { __refreshToday?: () => void }).__refreshToday;
      if (refresh) refresh();
    } catch (err) {
      console.error('[sync] failed', err);
      setSyncStatus('error');
    }
  };
  (window as unknown as { __triggerSync?: () => void }).__triggerSync = () => void triggerSync();

  setComposeCallbacks({
    onSubmit: async (content, source) => {
      const newMemo = createMemo({ userId: session.user.id, content, source });
      await putMemo(newMemo, { dirty: true });
      const refresh = (window as unknown as { __refreshToday?: () => void }).__refreshToday;
      if (refresh) refresh();
      void triggerSync();
    },
  });

  // 起動時に 1 回
  triggerSync();

  // 30 分ごと (タブが visible な時のみ)
  setInterval(() => {
    if (document.visibilityState === 'visible') triggerSync();
  }, 30 * 60 * 1000);

  // オフライン検知
  window.addEventListener('online', () => triggerSync());
  window.addEventListener('offline', () => setSyncStatus('offline'));

  mountAppShell(root, {
    today: renderTodayView,
    forest: renderForestView,
    settings: renderSettingsView,
  });
}

bootstrap().catch((err) => {
  console.error('Failed to bootstrap app', err);
  const root = document.getElementById('app');
  if (root) {
    root.innerHTML = `<pre style="color:red;padding:1rem">${String(err)}</pre>`;
  }
});
